//frontend/src/components/pages/PrivacyPolicy.js
import React from 'react';
import { 
    Shield, 
    Database, 
    Lock, 
    Mail, 
    Phone, 
    Bell, 
    FileText, 
    Clock, 
    Info, 
    BookOpen, 
    User,
    Settings,
    AlertTriangle,
    CheckCircle,
    Users,
    Cookie,
    MessageSquare,
    Share,
    ArrowRight,
    Copyright
} from 'lucide-react';
import { PrivacyPolicyMetaData } from '../layouts/MetaData/PrivacyPolicyMetaData';

const PrivacyPolicy = () => {
    return (
        <>
            <PrivacyPolicyMetaData />
            <div 
                className="pt-0"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header Section */}
                    <div className="text-center mb-10">
                        <h1 className="text-2xl md:text-3xl font-bold text-red-600 mb-3">
                            Privacy Policy
                        </h1>
                        <div className="text-base text-gray-600 max-w-3xl mx-auto">
                            <p>
                                This Privacy Policy explains how Vistara Farms Private Limited (referred to as "Meatbuddy," "Company," "we," "our," or "us") collects, uses, shares, and safeguards your personal information when you access or use our website (www.meatbuddy.in), mobile application, or mobile site (collectively, the "Platform"). By using our Platform, you consent to the collection and use of your information as outlined in this policy.
                            </p>
                        </div>
                    </div>

                    {/* Disclaimer Alert */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r-lg max-w-4xl mx-auto">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-xs text-yellow-700">
                                    We may update this policy at any time without prior notice, so please review it periodically. If you do not agree with any changes, you may discontinue using our services.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="space-y-6 max-w-4xl mx-auto">
                        {/* Information Collection */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Database className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Information We Collect</h2>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg">
                                    <User className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-sm mb-1">Account Registration & Orders</h3>
                                        <p className="text-gray-600 text-xs leading-relaxed">Name, phone number, email ID, address, and pin code</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg">
                                    <Lock className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-sm mb-1">Payment & Financial Information</h3>
                                        <p className="text-gray-600 text-xs leading-relaxed">Payments are securely processed via third-party providers (e.g., Razorpay). We do not store payment details.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg">
                                    <Settings className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-sm mb-1">Usage & Device Information</h3>
                                        <p className="text-gray-600 text-xs leading-relaxed">Browsing behavior, interaction patterns, IP address, device details, and location data</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg">
                                    <Cookie className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-sm mb-1">Cookies & Advertising Data</h3>
                                        <p className="text-gray-600 text-xs leading-relaxed">Used to enhance user experience, improve website functionality, and support targeted ads. You can manage or disable cookies in your browser settings at any time.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg">
                                    <MessageSquare className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-sm mb-1">Customer Support & Feedback</h3>
                                        <p className="text-gray-600 text-xs leading-relaxed">Information shared during interactions with our support team or through surveys.</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* How We Use Information */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Shield className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">How We Use Your Information</h2>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <span className="text-gray-600 text-xs leading-relaxed">Process orders and provide customer support</span>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <span className="text-gray-600 text-xs leading-relaxed">Improve our Platform and personalize your experience</span>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <span className="text-gray-600 text-xs leading-relaxed">Send promotional offers, updates, and transaction-related communications</span>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <span className="text-gray-600 text-xs leading-relaxed">Detect fraud, prevent security threats, and comply with legal obligations</span>
                                </li>
                            </ul>
                        </section>

                        {/* Sharing of Information */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Share className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Sharing of Information</h2>
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                We <strong>do not sell</strong> your personal data. However, we may share it with:
                            </p>
                            <ul className="space-y-3">
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <Users className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Third-Party Service Providers</p>
                                        <p className="text-gray-600 text-xs leading-relaxed">For payments, deliveries, and analytics</p>
                                    </div>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <FileText className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Legal Authorities</p>
                                        <p className="text-gray-600 text-xs leading-relaxed">If required by law to prevent fraud or ensure security</p>
                                    </div>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <ArrowRight className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Business Transfers</p>
                                        <p className="text-gray-600 text-xs leading-relaxed">In case of a merger or acquisition, your data may be transferred under this policy</p>
                                    </div>
                                </li>
                            </ul>
                        </section>

                        {/* Intellectual Property Section - NEWLY ADDED */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Copyright className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Intellectual Property Rights</h2>
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                All content on our Platform is protected by copyright, trademark, and other intellectual property laws.
                            </p>
                            <ul className="space-y-3">
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Brand Assets Protection</p>
                                        <p className="text-gray-600 text-xs leading-relaxed">The Meatbuddy name, logo, and all related branding elements are proprietary trademarks of Vistara Farms Private Limited. Unauthorized use, reproduction, or modification of these assets is strictly prohibited.</p>
                                    </div>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Product Images</p>
                                        <p className="text-gray-600 text-xs leading-relaxed">All product images on our Platform are owned by Meatbuddy and may not be copied, reproduced, or used for commercial or non-commercial purposes without our explicit written permission.</p>
                                    </div>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Website Content</p>
                                        <p className="text-gray-600 text-xs leading-relaxed">Text, graphics, user interface, visual interface, photographs, and other materials displayed on our Platform are proprietary to Meatbuddy and protected by copyright laws. Copying, scraping, or repurposing this content is prohibited without our prior written consent.</p>
                                    </div>
                                </li>
                                <li className="flex items-start bg-gray-50 p-3 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Legal Action</p>
                                        <p className="text-gray-600 text-xs leading-relaxed">Any unauthorized use of our intellectual property may result in legal action, including but not limited to claims for copyright infringement, trademark infringement, and unfair competition.</p>
                                    </div>
                                </li>
                            </ul>
                        </section>

                        {/* Data Security & Privacy */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Lock className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Data Security & Retention</h2>
                            </div>
                            <div className="space-y-4">
                                <p className="text-gray-600 text-xs leading-relaxed">
                                    We use encryption, access restrictions, and other security measures to protect your data. Your personal information is retained only as long as necessary for its intended purpose and is then securely deleted or anonymized.
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 text-sm mb-2">Your Rights</h3>
                                    <ul className="space-y-2">
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-red-600 mr-2" />
                                            <span className="text-gray-600 text-xs">Access, modify, or delete your personal data</span>
                                        </li>
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-red-600 mr-2" />
                                            <span className="text-gray-600 text-xs">Withdraw consent by contacting us at customersupport@meatbuddy.in with the subject "REMOVE"</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* Children's Privacy */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Users className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Children's Privacy</h2>
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                Our services are <strong>not intended for individuals under 18</strong>. If we become aware that a minor has provided personal data, we will promptly delete it.
                            </p>
                        </section>

                        {/* Contact Information */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Phone className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Grievance Redressal</h2>
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed mb-4">
                                For concerns or grievances regarding data privacy, contact:
                            </p>
                            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                                <div className="flex items-start">
                                    <User className="w-5 h-5 text-red-600 mr-2 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Grievance Officer:</p>
                                        <p className="text-gray-600 text-xs">Chethankumar</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <Mail className="w-5 h-5 text-red-600 mr-2 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Email:</p>
                                        <a href="mailto:customersupport@meatbuddy.in" className="text-red-600 hover:text-red-700 text-xs">
                                            customersupport@meatbuddy.in
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <Phone className="w-5 h-5 text-red-600 mr-2 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Phone:</p>
                                        <a href="tel:+916364749404" className="text-red-600 hover:text-red-700 text-xs">
                                        +91 6364749404
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <FileText className="w-5 h-5 text-red-600 mr-2 mt-0" />
                                    <div>
                                        <p className="font-semibold text-sm">Address:</p>
                                        <p className="text-gray-600 text-xs">Vistara Farms Private Limited,</p>
                                        <p className="text-gray-600 text-xs">No-158, Padma Upadyaya Layout,</p>
                                        <p className="text-gray-600 text-xs">Nagadevenahalli, Kengeri, Bangalore — 560056</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Compliance Notice */}
                        <div className="text-center text-xs text-gray-500 mt-8">
                            This Privacy Policy complies with the Information Technology Act, 2000, and related regulations.
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PrivacyPolicy;